import { createProxySignal, DocumentPrintData, MachineInfo, PrintRequest, requireContext, toast, uuidv4, visitorManagementClient, VisitorManagementBridgeStatus } from 'common';
import { Accessor, createContext, createEffect, createSignal, Setter } from 'solid-js'
import { createStore, SetStoreFunction, Store } from 'solid-js/store';
import { ActiveCheckinInfo, DocumentScanResult, SexOffender, VerificationResult, visitorCheckin as api } from 'st-app/api'
import { DocumentReaderContext } from '../st-document-reader/DocumentReaderContext';

export type VerificationStatus = 'verified' | 'dangerous' | 'verifying' | 'error' | 'pending'

export type VisitorStatus = 'PEND' | 'ALLOW' | 'DENY'

export type CheckInConfigurationHistoryEntry = {
    visitorType: string;
    reason: string;
    notes: string;
    meetingWith: string;
    meetingWithName: string;
}

export class VisitorCheckinContext {
    scan: Accessor<DocumentScanResult | undefined>; setScan: Setter<DocumentScanResult | undefined>;

    vmGU: Accessor<string>; setVmGU: Setter<string>;
    vmStationGU: Accessor<string | undefined>; setVmStationGU: Setter<string | undefined>;
    machineInfo: Accessor<MachineInfo | undefined>; setMachineInfo: Setter<MachineInfo | undefined>;
    vmBridgeStatus: Accessor<VisitorManagementBridgeStatus | undefined>; setVmBridgeStatus: Setter<VisitorManagementBridgeStatus | undefined>;
    isUpdatingVisitor: Accessor<boolean>; setIsUpdatingVisitor: Setter<boolean>;
    visitorStatus: Accessor<VisitorStatus | undefined>; setVisitorStatus: Setter<VisitorStatus | undefined>;
    activeCheckin: Accessor<ActiveCheckinInfo | undefined>; setActiveCheckin: Setter<ActiveCheckinInfo | undefined>;
    isVerifying: Accessor<boolean>; setIsVerifying: Setter<boolean>
    verificationStatusMessage: Accessor<string>; setVerificationStatusMessage: Setter<string>;
    verificationStatus: Accessor<VerificationStatus>; setVerificationStatus: Setter<VerificationStatus>;
    offenders: Accessor<SexOffender[]>; setOffenders: Setter<SexOffender[]>;
    printData: Store<DocumentPrintData>; setPrintData: SetStoreFunction<DocumentPrintData>;
    isPrinting: Accessor<boolean>; setIsPrinting: Setter<boolean>;

    cellPhone: Accessor<string>; setCellPhone: Setter<string>;
    email: Accessor<string>; setEmail: Setter<string>;
    visitorType: Accessor<string>; setVisitorType: Setter<string>;
    reason: Accessor<string>; setReason: Setter<string>;
    notes: Accessor<string>; setNotes: Setter<string>;
    meetingWith: Accessor<string>; setMeetingWith: Setter<string>;
    meetingWithName: Accessor<string>; setMeetingWithName: Setter<string>;
    selectedPrinterName: Accessor<string>; setSelectedPrinterName: Setter<string>;
    history: Accessor<CheckInConfigurationHistoryEntry[]>; setHistory: Setter<CheckInConfigurationHistoryEntry[]>;

    documentReader?: DocumentReaderContext;

    constructor() {
        [this.vmGU, this.setVmGU] = createSignal(uuidv4().toUpperCase());
        [this.vmStationGU, this.setVmStationGU] = createSignal<string | undefined>(
            localStorage.getItem('st-visitor-checkin.station-gu') ?? undefined
        );
        [this.machineInfo, this.setMachineInfo] = createSignal<MachineInfo | undefined>();
        [this.vmBridgeStatus, this.setVmBridgeStatus] = createSignal<VisitorManagementBridgeStatus | undefined>();
        [this.isUpdatingVisitor, this.setIsUpdatingVisitor] = createSignal<boolean>(false);
        [this.visitorStatus, this.setVisitorStatus] = createSignal<VisitorStatus>();
        [this.activeCheckin, this.setActiveCheckin] = createSignal<ActiveCheckinInfo>();

        createEffect(() => {
            const gu = this.vmStationGU();
            if (gu) {
                localStorage.setItem('st-visitor-checkin.station-gu', gu);
            }
        });
        [this.scan, this.setScan] = createSignal<DocumentScanResult>();
        [this.isVerifying, this.setIsVerifying] = createSignal<boolean>(false);
        [this.verificationStatusMessage, this.setVerificationStatusMessage] = createSignal<string>('');
        [this.verificationStatus, this.setVerificationStatus] = createSignal<VerificationStatus>('pending');
        [this.cellPhone, this.setCellPhone] = createSignal<string>('');
        [this.email, this.setEmail] = createSignal<string>('');
        [this.visitorType, this.setVisitorType] = createSignal<string>('');
        [this.reason, this.setReason] = createSignal<string>('');
        [this.notes, this.setNotes] = createSignal<string>('');
        [this.meetingWith, this.setMeetingWith] = createSignal<string>('');
        [this.meetingWithName, this.setMeetingWithName] = createSignal<string>('');
        [this.offenders, this.setOffenders] = createSignal<SexOffender[]>([]);
        [this.printData, this.setPrintData] = createStore<DocumentPrintData>({
            VisitorType: '',
            FirstName: '',
            LastName: '',
            CheckInDateTime: new Date().toLocaleString(),
            ImageBase64: '',
            MeetingWith: '',
            Reason: '',
            VisitorBarCode: this.vmGU(),
            GUID: this.vmGU(),
        });
        [this.isPrinting, this.setIsPrinting] = createSignal<boolean>(false);

        const preferredPrinter = localStorage.getItem('st-visitor-checkin.preferred-printer') || '';
        [this.selectedPrinterName, this.setSelectedPrinterName] = createSignal<string>(preferredPrinter);

        createEffect(() => {
            const s = this.scan();
            if (s) {
                this.setPrintData('FirstName', s.AAMVA.firstName ?? '');
                this.setPrintData('LastName', s.AAMVA.lastName ?? '');
                this.setPrintData('ImageBase64', s.photoBase64 ?? '');
                this.setPrintData('CheckInDateTime', new Date().toLocaleDateString());
            }
        });

        createEffect(() => {
            if (!this.selectedPrinterName()) return;
            localStorage.setItem('st-visitor-checkin.preferred-printer', this.selectedPrinterName());
        });

        [this.history, this.setHistory] = createSignal<CheckInConfigurationHistoryEntry[]>(this.getHistoryEntries());

        createEffect(() => {
            this.persistHistoryEntries(this.history());
        })
    }

    addHistoryEntry(entry: CheckInConfigurationHistoryEntry) {
        const history = this.history();
        const previous = history[0];

        if (previous && previous.visitorType === entry.visitorType
            && previous.reason === entry.reason
            && previous.notes === entry.notes
            && previous.meetingWith === entry.meetingWith) {
            return;
        }

        const maxEntries = 5;
        if (history.length + 1 > maxEntries) {
            history.pop();
        }

        this.setHistory([entry, ...this.history()]);
    }

    loadFromHistoryEntry(entry: CheckInConfigurationHistoryEntry) {
        this.setVisitorType(entry.visitorType);
        this.setReason(entry.reason);
        this.setNotes(entry.notes);
        this.setMeetingWith(entry.meetingWith);
        this.setMeetingWithName(entry.meetingWithName ?? '');
    }

    private getHistoryEntries(): CheckInConfigurationHistoryEntry[] {
        const raw = localStorage.getItem('st-visitor-checkin.history');
        return raw ? JSON.parse(raw) : [];
    }

    private persistHistoryEntries(entries: CheckInConfigurationHistoryEntry[]) {
        localStorage.setItem('st-visitor-checkin.history', JSON.stringify(entries));
    }

    onScan(scan: DocumentScanResult | undefined) {
        this.setScan(scan);
        this.setVisitorStatus(undefined);
        this.setActiveCheckin(undefined);
        this.setVerificationStatus('pending');
        this.setVerificationStatusMessage('');
        this.setOffenders([]);
    }

    async verify(): Promise<boolean> {
        this.setIsVerifying(true);
        this.setVerificationStatus('verifying');

        try {
            this.setVerificationStatusMessage('Running Sex Offender Registry Verification...')
            const sexOffenderVerification = await api.VerifySexOffender(this.scan()!);
            if (!sexOffenderVerification.passed) {
                this.setVerificationStatus('dangerous');
                this.setVerificationStatusMessage('Scanned ID failed sex offender verification test.');
                this.setOffenders(sexOffenderVerification.matchingOffenders);
                return false;
            }

            this.setVerificationStatus('verified');
            this.setVerificationStatusMessage('Verification Complete');

            return true;

        } catch(e) {
            console.error(e);
            toast.notify('error', 'An error occurred during the verification process.');
            this.setVerificationStatus('error')
            this.setVerificationStatusMessage('Internal Error Occurred');
            return false;
        } finally {
            this.setIsVerifying(false);
        }
    }

    async print() {
        if (this.isPrinting())
            return;

        this.setIsPrinting(true);
        try {
            const request: PrintRequest = {
                Data: this.printData,
                PrinterName: this.selectedPrinterName(),
            }

            this.addHistoryEntry({
                visitorType: this.visitorType(),
                reason: this.reason(),
                notes: this.notes(),
                meetingWith: this.meetingWith(),
                meetingWithName: this.meetingWithName(),
            });

            await visitorManagementClient.print(request);

            toast.notify('success', 'Printed ID card!')
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error ocurred while printing.');
        }
        this.setIsPrinting(false);
    }
}

export const VisitorCheckinContextJsx = createContext<VisitorCheckinContext>();
export const requireVisitorCheckinContext = () => {
    return requireContext(VisitorCheckinContextJsx);
}